import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, ArrowLeft, Mail, Check, Sun, Moon } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const { language, toggleLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'fr' ? 'Une erreur est survenue' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex flex-col`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Background effects */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
      )}

      {/* Header */}
      <header className="relative z-10 p-6 flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_exitcall/artifacts/v6rf7bvn_logo.svg" 
            alt="HICALL" 
            className="h-10"
          />
        </Link>
        <div className="flex items-center gap-2">
          <button 
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
            data-testid="theme-toggle"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button 
            onClick={toggleLanguage}
            className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-sm transition-colors`}
            data-testid="lang-toggle"
          >
            {language.toUpperCase()}
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-lg'} rounded-2xl border p-8`}>
            
            {!success ? (
              <>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 bg-[#3272F1] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                    {language === 'fr' ? 'Mot de passe oublié' : 'Forgot Password'}
                  </h1>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-600'}>
                    {language === 'fr' 
                      ? 'Entrez votre email pour recevoir un lien de réinitialisation'
                      : 'Enter your email to receive a reset link'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm" data-testid="forgot-error">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                      {language === 'fr' ? 'Email' : 'Email'}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                      required
                      className={`${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#3272F1] focus:ring-[#3272F1]`}
                      data-testid="email-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#3272F1] hover:bg-[#2560d9] text-white py-6 rounded-xl font-semibold" 
                    disabled={loading}
                    data-testid="forgot-submit"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      language === 'fr' ? 'Envoyer le lien' : 'Send reset link'
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="w-14 h-14 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-green-500" />
                </div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {language === 'fr' ? 'Email envoyé !' : 'Email sent!'}
                </h2>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
                  {language === 'fr' 
                    ? 'Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.'
                    : 'If an account exists with this email, you will receive a reset link.'}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {language === 'fr' 
                    ? 'Vérifiez également vos spams'
                    : 'Also check your spam folder'}
                </p>
              </div>
            )}

            {/* Back to login link */}
            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className={`inline-flex items-center gap-2 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
              >
                <ArrowLeft className="w-4 h-4" />
                {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
